/**
 * FacadeKanban - Kanban board LiveView hook
 *
 * Manages drag-and-drop (SortableJS), bulk selection (Shift+click),
 * and delegated context menus for kanban card elements.
 *
 * ## DOM Contract
 *
 * ```html
 * <div phx-hook="FacadeKanban">                    <!-- board root -->
 *   <div data-kanban-column data-status="todo">    <!-- column -->
 *     <div data-kanban-card data-card-id="uuid">   <!-- card -->
 *       <template data-part="context-menu-items">  <!-- menu -->
 *     </div>
 *   </div>
 * </div>
 * ```
 *
 * ## LiveView Events
 *
 * - `board_move`         — `{ data: { id, from_group_id, to_group_id, index } }`
 * - `board_reorder`      — `{ data: { id, index } }`
 * - `board_bulk_move`    — `{ data: { ids, to_group_id, index? } }` (index omitted = append at end)
 * - `bulk_delete_tasks`  — `{ ids: string[] }`
 */

import type { SortableEvent } from 'sortablejs'
import { pushEvent, type ViewHook } from '../helpers/utils'

// ── Constants ────────────────────────────────────────────────────────

const SEL = {
  column: '[data-kanban-column]',
  card: '[data-kanban-card]',
  menuTemplate: 'template[data-part="context-menu-items"]',
  menuItem: '[role="menuitem"]',
  focusable: '[role="menuitem"]:not([aria-disabled="true"])',
  bulkHidden: '[data-bulk-hidden]'
} as const

const ATTRS = {
  selected: 'data-selected',
  bulkHidden: 'data-bulk-hidden',
  bulkBadge: 'data-bulk-badge'
} as const

const PHX_VALUE_PREFIX = 'phx-value-'

const SORTABLE_CONFIG = {
  group: 'kanban-board',
  animation: 150,
  ghostClass: 'opacity-30',
  chosenClass: 'shadow-lg',
  dragClass: 'rotate-2',
  easing: 'cubic-bezier(0.2, 0, 0, 1)'
} as const

const Z_INDEX_FLOATING = '9999' as const

const MENU_PANEL_STYLE = {
  zIndex: Z_INDEX_FLOATING,
  fontSize: '13px',
  minWidth: '180px',
  padding: '6px'
} as const

// ── Types ────────────────────────────────────────────────────────────

interface SortableInstance {
  destroy: () => void
}

type Cleanup = () => void

// ── DOM Helpers ──────────────────────────────────────────────────────

/** Collect phx-value-* attributes into a flat object */
const extractPhxValues = (el: HTMLElement): Record<string, string> =>
  Object.fromEntries(
    Array.from(el.attributes)
      .filter((a) => a.name.startsWith(PHX_VALUE_PREFIX))
      .map((a) => [a.name.slice(PHX_VALUE_PREFIX.length), a.value])
  )

// ── Selection ────────────────────────────────────────────────────────

const isSelected = (card: HTMLElement): boolean =>
  card.hasAttribute(ATTRS.selected)

const selectCard = (card: HTMLElement): void => {
  card.setAttribute(ATTRS.selected, '')
}

const deselectCard = (card: HTMLElement): void => {
  card.removeAttribute(ATTRS.selected)
}

const toggleCard = (card: HTMLElement): void =>
  isSelected(card) ? deselectCard(card) : selectCard(card)

const getSelected = (board: HTMLElement): readonly HTMLElement[] =>
  Array.from(
    board.querySelectorAll<HTMLElement>(`${SEL.card}[${ATTRS.selected}]`)
  )

const getSelectedIds = (board: HTMLElement): readonly string[] =>
  getSelected(board).flatMap((c) =>
    c.dataset.cardId ? [c.dataset.cardId] : []
  )

const clearSelection = (board: HTMLElement): void => {
  for (const card of getSelected(board)) {
    deselectCard(card)
  }
}

/** All cards in DOM order within the board */
const allCards = (board: HTMLElement): readonly HTMLElement[] =>
  Array.from(board.querySelectorAll<HTMLElement>(SEL.card))

/** Select a range of cards between two cards (inclusive), by DOM position */
const selectRange = (
  board: HTMLElement,
  from: HTMLElement,
  to: HTMLElement
): void => {
  const cards = allCards(board)
  const fromIdx = cards.indexOf(from)
  const toIdx = cards.indexOf(to)
  if (fromIdx === -1 || toIdx === -1) {
    return
  }

  const start = Math.min(fromIdx, toIdx)
  const end = Math.max(fromIdx, toIdx)

  for (let i = start; i <= end; i++) {
    selectCard(cards[i])
  }
}

// ── Bulk Drag Visuals ────────────────────────────────────────────────

const showBulkDrag = (item: HTMLElement, count: number): void => {
  item.setAttribute('data-bulk-drag', '')

  const badge = document.createElement('div')
  badge.setAttribute(ATTRS.bulkBadge, '')
  badge.textContent = String(count)
  item.appendChild(badge)
}

const hideBulkDrag = (item: HTMLElement): void => {
  item.removeAttribute('data-bulk-drag')
  item.querySelector(`[${ATTRS.bulkBadge}]`)?.remove()
}

const hideOtherSelected = (board: HTMLElement, exceptId: string): void => {
  for (const card of getSelected(board)) {
    if (card.dataset.cardId !== exceptId) {
      card.setAttribute(ATTRS.bulkHidden, '')
    }
  }
}

const restoreHidden = (board: HTMLElement): void => {
  for (const card of board.querySelectorAll<HTMLElement>(SEL.bulkHidden)) {
    card.removeAttribute(ATTRS.bulkHidden)
  }
}

// ── Drag Start Handler ───────────────────────────────────────────────

const onDragStart = (event: SortableEvent, board: HTMLElement): void => {
  const cardId = event.item.dataset.cardId
  if (!cardId) {
    return
  }

  const ids = getSelectedIds(board)
  if (ids.length > 1 && ids.includes(cardId)) {
    showBulkDrag(event.item, ids.length)
    hideOtherSelected(board, cardId)
  }
}

// ── Hook ─────────────────────────────────────────────────────────────

export const FacadeKanban = {
  _sortables: undefined as SortableInstance[] | undefined,
  _cleanups: undefined as Cleanup[] | undefined,
  _destroyed: false,

  async mounted() {
    this._sortables = []
    this._cleanups = []
    this._destroyed = false

    const { default: Sortable } = await import('sortablejs')
    if (this._destroyed) {
      return
    }
    const board = this.el as HTMLElement
    const hook = this as unknown as ViewHook

    // Clean up any stale bulk-hidden state from a prior session
    restoreHidden(board)

    // Helper: register listener + auto-cleanup
    const listen = <K extends keyof HTMLElementEventMap>(
      target: HTMLElement | Document,
      event: K,
      handler: (e: HTMLElementEventMap[K]) => void,
      capture = false
    ): void => {
      target.addEventListener(event, handler as EventListener, capture)
      this._cleanups!.push(() =>
        target.removeEventListener(event, handler as EventListener, capture)
      )
    }

    // Selection state (anchor lives here, reset closes over it)
    let anchor: HTMLElement | null = null

    const resetBulkState = (): void => {
      restoreHidden(board)
      clearSelection(board)
      anchor = null
    }

    // ── Context Menu (per-instance, closes over board/hook/resetBulkState) ──

    let activeClose: Cleanup | null = null

    const openContextMenu = (card: HTMLElement, x: number, y: number): void => {
      activeClose?.()

      const template = card.querySelector<HTMLTemplateElement>(SEL.menuTemplate)
      if (!template) {
        return
      }

      // Build panel
      const panel = document.createElement('div')
      panel.setAttribute('role', 'menu')
      panel.setAttribute('data-floating', '')
      panel.className = 'context-menu-panel'
      Object.assign(panel.style, MENU_PANEL_STYLE)
      panel.appendChild(
        template.content?.cloneNode(true) ?? template.cloneNode(true)
      )
      document.body.appendChild(panel)

      // Position near cursor, flip at edges
      Object.assign(panel.style, {
        position: 'fixed',
        visibility: 'hidden',
        left: '0',
        top: '0'
      })
      const rect = panel.getBoundingClientRect()
      Object.assign(panel.style, {
        visibility: '',
        left: `${x + rect.width > innerWidth ? Math.max(0, x - rect.width) : x}px`,
        top: `${y + rect.height > innerHeight ? Math.max(0, y - rect.height) : y}px`
      })

      panel.querySelector<HTMLElement>(SEL.focusable)?.focus()

      // Close + cleanup
      const close = (): void => {
        panel.remove()
        document.removeEventListener('click', onOutside, true)
        document.removeEventListener('keydown', onKey, true)
        window.removeEventListener('scroll', close, true)
        if (activeClose === close) {
          activeClose = null
        }
      }

      const onOutside = (e: MouseEvent): void => {
        if (!panel.contains(e.target as Node)) {
          close()
        }
      }
      const onKey = (e: KeyboardEvent): void => {
        if (e.key === 'Escape') {
          e.preventDefault()
          close()
        }
      }

      // Item click (delegated) - bulk-aware
      const ids = getSelectedIds(board)
      const isBulk =
        ids.length > 1 &&
        card.dataset.cardId &&
        ids.includes(card.dataset.cardId)

      panel.addEventListener('click', (ev) => {
        const item = (ev.target as HTMLElement).closest<HTMLElement>(
          SEL.menuItem
        )
        if (!item || item.getAttribute('aria-disabled') === 'true') {
          return
        }
        const event = item.getAttribute('phx-click')
        if (!event) {
          close()
          return
        }

        const skipPerItemConfirm = isBulk && event === 'delete_task'
        if (
          !skipPerItemConfirm &&
          item.dataset.confirm &&
          !confirm(item.dataset.confirm)
        ) {
          return
        }

        if (isBulk && event === 'board_change_status') {
          // Bulk status change — no index; server appends at end
          const status = extractPhxValues(item).status
          pushEvent(hook, 'board_bulk_move', {
            data: { ids, to_group_id: status }
          })
          resetBulkState()
        } else if (isBulk && event === 'delete_task') {
          if (!confirm(`Delete ${ids.length} tasks?`)) {
            return
          }
          pushEvent(hook, 'bulk_delete_tasks', { ids })
          resetBulkState()
        } else {
          pushEvent(hook, event, extractPhxValues(item))
        }
        close()
      })

      document.addEventListener('click', onOutside, true)
      document.addEventListener('keydown', onKey, true)
      window.addEventListener('scroll', close, true)
      activeClose = close
    }

    // Ensure context menu is closed when hook is destroyed
    this._cleanups!.push(() => activeClose?.())

    // ── Drag End Handler (closes over resetBulkState) ────────────────

    const onDragEnd = (event: SortableEvent): void => {
      // Always clean up visuals first
      hideBulkDrag(event.item)
      restoreHidden(board)

      const cardId = event.item.dataset.cardId
      if (!cardId) {
        return
      }

      const from = event.from.dataset.status
      const to = event.to.dataset.status
      const ids = getSelectedIds(board)

      // Bulk move
      if (ids.length > 1 && ids.includes(cardId)) {
        pushEvent(hook, 'board_bulk_move', {
          data: { ids, to_group_id: to, index: event.newIndex }
        })
        resetBulkState()
        return
      }

      // Single card
      if (from === to) {
        pushEvent(hook, 'board_reorder', {
          data: { id: cardId, index: event.newIndex }
        })
      } else {
        pushEvent(hook, 'board_move', {
          data: {
            id: cardId,
            from_group_id: from,
            to_group_id: to,
            index: event.newIndex
          }
        })
      }
    }

    listen(
      board,
      'click',
      (e: MouseEvent) => {
        const card = (e.target as HTMLElement).closest<HTMLElement>(SEL.card)
        if (!card) {
          // Click inside board but not on a card - clear selection
          if (!e.shiftKey && getSelected(board).length > 0) {
            resetBulkState()
          }
          return
        }
        if (e.shiftKey) {
          e.preventDefault()
          e.stopPropagation()

          // Range select if we have a valid anchor still in the DOM
          if (anchor && anchor !== card && board.contains(anchor)) {
            clearSelection(board)
            selectRange(board, anchor, card)
          } else {
            toggleCard(card)
          }
          anchor = card
        } else {
          // Plain click: clear selection and set new anchor
          clearSelection(board)
          anchor = card
        }
      },
      true
    )

    // Escape fully resets bulk state
    listen(document, 'keydown', (e: KeyboardEvent) => {
      if (e.key === 'Escape' && getSelected(board).length > 0) {
        resetBulkState()
      }
    })

    // Outside click clears bulk selection (ignore clicks on floating panels)
    listen(document, 'click', (e: MouseEvent) => {
      const target = e.target as HTMLElement | null
      if (!target || board.contains(target)) {
        return
      }
      if (target.closest('[data-floating], .context-menu-panel')) {
        return
      }
      if (getSelected(board).length === 0) {
        return
      }
      resetBulkState()
    })

    // Delegated context menu
    listen(board, 'contextmenu', (e: MouseEvent) => {
      const card = (e.target as HTMLElement).closest<HTMLElement>(SEL.card)
      if (!card) {
        return
      }
      e.preventDefault()
      openContextMenu(card, e.clientX, e.clientY)
    })

    // SortableJS per column
    this._sortables = Array.from(board.querySelectorAll(SEL.column)).map(
      (col) =>
        Sortable.create(col as HTMLElement, {
          ...SORTABLE_CONFIG,
          draggable: SEL.card,
          onStart: (ev: SortableEvent) => onDragStart(ev, board),
          onEnd: (ev: SortableEvent) => onDragEnd(ev)
        })
    )
  },

  destroyed() {
    this._destroyed = true
    for (const s of this._sortables ?? []) {
      s.destroy()
    }
    this._sortables = []
    for (const fn of this._cleanups ?? []) {
      fn()
    }
    this._cleanups = []
  }
}
